import { softwareList } from "@/mocks/software";
import { categories } from "@/mocks/categories";

export interface Software {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
  subCategory: string;
  cover: string;
  screenshots: string[];
  intro: string;
  description: string;
  features: string[];
  version: string;
  size: string;
  platforms: string[];
  updatedAt: string;
  downloads: number;
  rating: number;
  free: boolean;
  officialUrl: string;
  tags: string[];
}

export interface Category {
  id: string;
  name: string;
  shortName: string;
  icon: string;
  tone: string;
  desc: string;
  subCategories: string[];
}

export type SortKey = "latest" | "downloads" | "rating";

export const allSoftware = softwareList as Software[];
export const allCategories = categories as Category[];

export function getCategoryById(id: string): Category | undefined {
  return allCategories.find((item) => item.id === id);
}

export function getSoftwareById(id: string): Software | undefined {
  return allSoftware.find((item) => item.id === id);
}

export function getCategoryCount(categoryId: string): number {
  return allSoftware.filter((item) => item.categoryId === categoryId).length;
}

export function sortSoftware(list: Software[], sort: SortKey): Software[] {
  const cloned = [...list];
  if (sort === "downloads") {
    return cloned.sort((a, b) => b.downloads - a.downloads);
  }
  if (sort === "rating") {
    return cloned.sort((a, b) => b.rating - a.rating);
  }
  return cloned.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export interface FilterOptions {
  categoryId?: string;
  subCategory?: string;
  sort?: SortKey;
  keyword?: string;
  freeOnly?: boolean;
}

export function filterSoftware(options: FilterOptions): Software[] {
  const { categoryId, subCategory, sort = "latest", keyword = "", freeOnly = true } = options;
  const kw = keyword.trim().toLowerCase();
  let list = allSoftware.filter((item) => {
    if (categoryId && item.categoryId !== categoryId) return false;
    if (subCategory && item.subCategory !== subCategory) return false;
    if (freeOnly && !item.free) return false;
    if (kw) {
      const haystack = [
        item.name,
        item.intro,
        item.categoryName,
        item.subCategory,
        item.tags.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });
  list = sortSoftware(list, sort);
  return list;
}

export function getRelatedSoftware(item: Software, limit = 3): Software[] {
  const sameCategory = allSoftware.filter(
    (s) => s.categoryId === item.categoryId && s.id !== item.id
  );
  const sorted = sortSoftware(sameCategory, "downloads");
  if (sorted.length >= limit) return sorted.slice(0, limit);
  const others = allSoftware.filter(
    (s) => s.categoryId !== item.categoryId && s.id !== item.id
  );
  return [...sorted, ...sortSoftware(others, "downloads")].slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function formatDownloads(value: number): string {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(1)}亿`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return `${value}`;
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

export function newsOfSoftware(list: Software[], limit: number): Software[] {
  return sortSoftware(list, "latest").slice(0, limit);
}

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按发布时间" },
  { key: "downloads", label: "按下载量" },
  { key: "rating", label: "按评分" },
];